import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { energyFromMacros } from '@nutrigeist/shared';
import { authApi, exportApi, intakeApi } from '../api/endpoints.js';
import { ApiError } from '../api/client.js';
import { useAuth, useCurrentUser } from '../auth/AuthContext.js';
import { formatKcal } from '../lib/format.js';
import { BUILD_ID, useAppVersion } from '../lib/appVersion.js';
import { ApiTokens } from '../components/ApiTokens.js';

/** The boolean preferences that appear as switches under "Funktionen". */
type FeatureKey = 'trackBodyFat' | 'adaptiveTdee' | 'offlineQueue';

/** Empty string means "no goal"; the API stores that as null. */
type GoalField = 'kcal' | 'protein' | 'carbs' | 'fat' | 'fiber' | 'weightKg' | 'waterMl';

const GOAL_FIELDS: { key: GoalField; label: string; unit: string; placeholder?: string }[] = [
  { key: 'kcal', label: 'Kalorien', unit: 'kcal' },
  { key: 'protein', label: 'Eiweiß', unit: 'g' },
  { key: 'carbs', label: 'Kohlenhydrate', unit: 'g' },
  { key: 'fat', label: 'Fett', unit: 'g' },
  // Unset falls back to the DGE recommendation rather than disabling the bar,
  // so the placeholder states what leaving it empty actually means.
  { key: 'fiber', label: 'Ballaststoffe', unit: 'g', placeholder: '30 (DGE-Empfehlung)' },
  { key: 'weightKg', label: 'Zielgewicht', unit: 'kg' },
  { key: 'waterMl', label: 'Wasser', unit: 'ml' },
];

export function SettingsPage(): JSX.Element {
  const user = useCurrentUser();
  const { logout, setUser } = useAuth();

  const [displayName, setDisplayName] = useState(user.displayName);
  const [goals, setGoals] = useState<Record<GoalField, string>>(() => ({
    kcal: user.goals.kcal?.toString() ?? '',
    protein: user.goals.protein?.toString() ?? '',
    carbs: user.goals.carbs?.toString() ?? '',
    fat: user.goals.fat?.toString() ?? '',
    fiber: user.goals.fiber?.toString() ?? '',
    weightKg: user.goals.weightKg?.toString() ?? '',
    waterMl: user.goals.waterMl?.toString() ?? '',
  }));
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [checkResult, setCheckResult] = useState<string | null>(null);

  const trackBodyFat = user.preferences.trackBodyFat ?? false;
  const adaptiveTdee = user.preferences.adaptiveTdee ?? false;
  const offlineQueue = user.preferences.offlineQueue ?? false;

  // Saved immediately rather than behind the "Speichern" button below: these
  // are switches, and a switch that needs confirming elsewhere reads as broken.
  const togglePreference = useMutation({
    mutationFn: ({ key, enabled }: { key: FeatureKey; enabled: boolean }) =>
      authApi.updateProfile({ preferences: { [key]: enabled } }),
    onSuccess: (updated) => {
      setUser(updated);
      setError(null);
    },
    onError: (caught) =>
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Einstellung konnte nicht gespeichert werden.',
      ),
  });

  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = useMutation({
    mutationFn: (kind: 'archive' | 'diary' | 'weight') => {
      if (kind === 'diary') return exportApi.diaryCsv();
      if (kind === 'weight') return exportApi.weightCsv();
      return exportApi.archive();
    },
    onSuccess: () => setDownloadError(null),
    onError: (caught) =>
      setDownloadError(
        caught instanceof ApiError ? caught.message : 'Export fehlgeschlagen. Erneut versuchen?',
      ),
  });

  const version = useAppVersion();

  const visionHealth = useQuery({
    queryKey: ['vision', 'health'],
    queryFn: () => intakeApi.visionHealth(),
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const parsed = Object.fromEntries(
        GOAL_FIELDS.map(({ key }) => {
          const raw = goals[key].trim().replace(',', '.');
          return [key, raw === '' ? null : Number(raw)];
        }),
      ) as Record<GoalField, number | null>;

      for (const [key, value] of Object.entries(parsed)) {
        if (value !== null && !Number.isFinite(value)) {
          throw new Error(`"${key}" ist keine gültige Zahl.`);
        }
      }

      return authApi.updateProfile({ displayName: displayName.trim(), goals: parsed });
    },
    onSuccess: (updated) => {
      setUser(updated);
      setStatus('Gespeichert.');
      setError(null);
    },
    onError: (caught) => {
      setStatus(null);
      setError(caught instanceof ApiError ? caught.message : (caught as Error).message);
    },
  });

  // Sanity check shown inline: do the macro goals actually add up to the
  // calorie goal? Off-by-a-lot usually means a typo rather than an intent.
  const macroImpliedKcal = energyFromMacros({
    kcal: 0,
    protein: Number(goals.protein.replace(',', '.')) || 0,
    carbs: Number(goals.carbs.replace(',', '.')) || 0,
    sugars: 0,
    fat: Number(goals.fat.replace(',', '.')) || 0,
    saturated: 0,
    fiber: 0,
    salt: 0,
  });
  const kcalGoal = Number(goals.kcal.replace(',', '.')) || 0;
  const macrosDisagree =
    kcalGoal > 0 && macroImpliedKcal > 0 && Math.abs(macroImpliedKcal - kcalGoal) / kcalGoal > 0.1;

  return (
    <div className="stack">
      <h1>Profil</h1>

      <div className="card stack">
        <div className="card-title">Konto</div>

        <div className="field">
          <label htmlFor="displayName">Name</label>
          <input
            id="displayName"
            className="input"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
        </div>

        <div className="field">
          <label>E-Mail</label>
          <span className="hint">{user.email}</span>
        </div>

        <div className="field">
          <label>Zeitzone</label>
          <span className="hint">{user.preferences.timezone}</span>
        </div>
      </div>

      <div className="card stack">
        <div className="card-title">Funktionen</div>

        <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={trackBodyFat}
            disabled={togglePreference.isPending}
            onChange={(event) =>
              togglePreference.mutate({ key: 'trackBodyFat', enabled: event.target.checked })
            }
            style={{ marginTop: 4 }}
          />
          <span className="grow">
            <span>Körperfettanteil mitverfolgen</span>
            <span className="hint" style={{ display: 'block' }}>
              Blendet bei <strong>Gewicht</strong> ein zweites Feld für den Körperfettanteil ein.
              Ausschalten blendet es wieder aus; bereits erfasste Werte bleiben erhalten.
            </span>
          </span>
        </label>

        <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={adaptiveTdee}
            disabled={togglePreference.isPending}
            onChange={(event) =>
              togglePreference.mutate({ key: 'adaptiveTdee', enabled: event.target.checked })
            }
            style={{ marginTop: 4 }}
          />
          <span className="grow">
            <span>Adaptiver Kalorienverbrauch</span>
            <span className="hint" style={{ display: 'block' }}>
              Errechnet deinen tatsächlichen Verbrauch aus dem, was du gegessen hast, und dem
              Verlauf deines Gewichts — statt ihn aus einer Formel zu schätzen. Erscheint unter{' '}
              <strong>Verlauf</strong>. Braucht rund vier Wochen mit fast lückenlosem Tagebuch und
              regelmäßigem Wiegen; vorher wird bewusst keine Zahl angezeigt.
            </span>
          </span>
        </label>

        <label className="row" style={{ gap: 10, alignItems: 'flex-start' }}>
          <input
            type="checkbox"
            checked={offlineQueue}
            disabled={togglePreference.isPending}
            onChange={(event) =>
              togglePreference.mutate({ key: 'offlineQueue', enabled: event.target.checked })
            }
            style={{ marginTop: 4 }}
          />
          <span className="grow">
            <span>Offline-Warteschlange</span>
            <span className="hint" style={{ display: 'block' }}>
              Einträge, Wasser, Gewicht und Aktivität lassen sich ohne Verbindung erfassen und
              werden später automatisch übertragen. Beachte: bis dahin liegen sie nur auf diesem
              Gerät. Wartende Einträge werden oben auf jeder Seite angezeigt.
            </span>
          </span>
        </label>
      </div>

      <div className="card stack">
        <div className="card-title">Tagesziele</div>
        <span className="hint">Leer lassen, um ein Ziel zu deaktivieren.</span>

        {GOAL_FIELDS.map(({ key, label, unit, placeholder }) => (
          <div className="field" key={key}>
            <label htmlFor={`goal-${key}`}>
              {label} ({unit})
            </label>
            <input
              id={`goal-${key}`}
              className="input"
              inputMode="decimal"
              placeholder={placeholder}
              value={goals[key]}
              onChange={(event) =>
                setGoals((current) => ({ ...current, [key]: event.target.value }))
              }
            />
          </div>
        ))}

        {macrosDisagree && (
          <div className="banner banner-warning">
            Die Makroziele ergeben {formatKcal(macroImpliedKcal)} kcal, das Kalorienziel steht auf{' '}
            {formatKcal(kcalGoal)} kcal.
          </div>
        )}

        {status && (
          <div className="banner banner-info" role="status">
            {status}
          </div>
        )}
        {error && (
          <div className="banner banner-error" role="alert">
            {error}
          </div>
        )}

        <button
          className="btn btn-primary btn-block"
          disabled={save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? <span className="spinner" /> : 'Speichern'}
        </button>
      </div>

      <div className="card stack">
        <div className="card-title">Daten & Backup</div>
        <span className="hint">
          Alles, was du erfasst hast — zum Mitnehmen. Das JSON enthält Tagebuch, Gewicht, Wasser,
          Rezepte, eigene Lebensmittel und deine Ziele. Passwörter sind nie enthalten.
        </span>

        <button
          className="btn btn-primary btn-block"
          disabled={download.isPending}
          onClick={() => download.mutate('archive')}
        >
          {download.isPending && download.variables === 'archive' ? (
            <span className="spinner" />
          ) : (
            'Komplettes Backup (JSON)'
          )}
        </button>

        <div className="row">
          <button
            className="btn btn-ghost grow"
            disabled={download.isPending}
            onClick={() => download.mutate('diary')}
          >
            Tagebuch (CSV)
          </button>
          <button
            className="btn btn-ghost grow"
            disabled={download.isPending}
            onClick={() => download.mutate('weight')}
          >
            Gewicht (CSV)
          </button>
        </div>

        {downloadError && (
          <div className="banner banner-error" role="alert">
            {downloadError}
          </div>
        )}
      </div>

      <ApiTokens />

      <div className="card stack">
        <div className="card-title">Label-Scanner</div>
        {visionHealth.isPending && <span className="hint">Status wird geprüft…</span>}
        {visionHealth.data && (
          <>
            <div className="row-between">
              <span>{visionHealth.data.model}</span>
              <span
                className={
                  visionHealth.data.reachable ? 'badge badge-accent' : 'badge badge-warning'
                }
              >
                {visionHealth.data.reachable ? 'bereit' : 'nicht verfügbar'}
              </span>
            </div>

            {/* Which provider would actually answer. Worth saying out loud:
                the hosted one costs money per scan and sends the photo off the
                LAN, so running on it unnoticed is the thing to avoid. */}
            {visionHealth.data.provider === 'openrouter' && (
              <span className="hint">
                Läuft gerade über OpenRouter — lokal ist kein Knoten erreichbar. Jeder Scan wird
                abgerechnet und das Foto verlässt das Netz.
              </span>
            )}

            {visionHealth.data.provider === 'ollama' && visionHealth.data.fallback.configured && (
              <span className="hint">
                Lokal. OpenRouter steht als Reserve bereit
                {visionHealth.data.fallback.model ? ` (${visionHealth.data.fallback.model})` : ''}.
              </span>
            )}

            {!visionHealth.data.reachable && !visionHealth.data.fallback.configured && (
              <span className="hint">
                Kein Fallback eingerichtet. Ein OPENROUTER_API_KEY im Stack macht Scannen auch dann
                möglich, wenn der Desktop aus ist.
              </span>
            )}

            {visionHealth.data.fallback.error && (
              <span className="hint" style={{ color: 'var(--danger)' }}>
                OpenRouter: {visionHealth.data.fallback.error}
              </span>
            )}

            {/* Nodes are listed in preference order: the first one that can
                serve a scan gets it, so the order is operationally meaningful. */}
            {visionHealth.data.nodes.map((node, index) => (
              <div className="row-between" key={node.endpoint}>
                <span className="hint">
                  {index + 1}. {node.endpoint}
                </span>
                <span
                  className={
                    node.reachable && node.hasModel ? 'badge badge-accent' : 'badge badge-warning'
                  }
                >
                  {!node.reachable ? 'offline' : node.hasModel ? 'ok' : 'Modell fehlt'}
                </span>
              </div>
            ))}
          </>
        )}
      </div>

      <div className="card stack">
        <div className="card-title">App-Version</div>

        <div className="row-between">
          <span className="hint">Installiert</span>
          <span className="hint">{BUILD_ID}</span>
        </div>

        {version.serverBuildId && version.serverBuildId !== BUILD_ID && (
          <div className="row-between">
            <span className="hint">Auf dem Server</span>
            <span className="hint">{version.serverBuildId}</span>
          </div>
        )}

        {checkResult && (
          <div
            className={version.outdated ? 'banner banner-warning' : 'banner banner-info'}
            role="status"
          >
            {checkResult}
          </div>
        )}

        {version.outdated ? (
          <button className="btn btn-primary btn-block" onClick={() => void version.reload()}>
            Jetzt aktualisieren
          </button>
        ) : (
          <button
            className="btn btn-ghost btn-block"
            disabled={version.checking}
            onClick={() => {
              void version.check().then((available) => {
                setCheckResult(
                  available
                    ? 'Eine neue Version ist verfügbar.'
                    : 'Du verwendest bereits die neueste Version.',
                );
              });
            }}
          >
            {version.checking ? <span className="spinner" /> : 'Nach Updates suchen'}
          </button>
        )}
      </div>

      <button className="btn btn-danger btn-block" onClick={() => void logout()}>
        Abmelden
      </button>
    </div>
  );
}
